import { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Stack,
  Alert,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from "@mui/material";
import { CheckCircle } from "@mui/icons-material";
import { QRCodeSVG } from "qrcode.react";
import { enqueueSnackbar } from "notistack";
import { useQueryClient } from "@tanstack/react-query";
import {
  AUTHENTICATOR_STATUS_KEY,
  useAuthenticatorStatus,
  useAuthenticatorEnrollStart,
  useAuthenticatorEnrollVerify,
  useDisableAuthenticator,
} from "../api/hooks";

const errMsg = (e: any) =>
  typeof e?.response?.data === "string"
    ? e.response.data
    : e?.response?.data?.message || e?.message || "Something went wrong.";

const OUTLINED_BTN = {
  borderRadius: "10px",
  textTransform: "none" as const,
  borderColor: "#123257",
  color: "#123257",
  px: 2.5,
  fontWeight: 500,
};

// Authenticator (TOTP) enrollment — feature/totp-mfa. Rendered inside the Security Settings page,
// styled to match the surrounding Login Information rows.
export const AuthenticatorSetup: React.FC = () => {
  const queryClient = useQueryClient();
  const { data: status, isLoading } = useAuthenticatorStatus();

  const [enrollOpen, setEnrollOpen] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);
  const [uri, setUri] = useState("");
  const [manualKey, setManualKey] = useState("");
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [disableCode, setDisableCode] = useState("");

  const startM = useAuthenticatorEnrollStart();
  const verifyM = useAuthenticatorEnrollVerify();
  const disableM = useDisableAuthenticator();

  const refreshStatus = () =>
    queryClient.invalidateQueries({ queryKey: AUTHENTICATOR_STATUS_KEY });

  const openEnroll = () => {
    setCode("");
    setBackupCodes(null);
    setUri("");
    setManualKey("");
    setEnrollOpen(true);
    startM.mutate(undefined, {
      onSuccess: (d) => {
        setUri(d.otpAuthUri);
        setManualKey(d.manualEntryKey);
      },
      onError: (e) => {
        enqueueSnackbar(errMsg(e), { variant: "error" });
        setEnrollOpen(false);
      },
    });
  };

  const verifyEnroll = () => {
    if (code.length !== 6) return;
    verifyM.mutate(
      { code },
      {
        onSuccess: (d) => {
          setBackupCodes(d.recoveryCodes);
          refreshStatus();
          enqueueSnackbar("Authenticator enabled.", { variant: "success" });
        },
        onError: (e) => enqueueSnackbar(errMsg(e), { variant: "error" }),
      },
    );
  };

  const doDisable = () => {
    if (!disableCode.trim()) return;
    disableM.mutate(
      { code: disableCode.trim() },
      {
        onSuccess: () => {
          setDisableOpen(false);
          setDisableCode("");
          refreshStatus();
          enqueueSnackbar("Authenticator disabled.", { variant: "success" });
        },
        onError: (e) => enqueueSnackbar(errMsg(e), { variant: "error" }),
      },
    );
  };

  // Hidden for accounts that aren't allowed to use authenticator MFA.
  if (!isLoading && status && !status.eligible) return null;

  const enabled = !!status?.enabled;
  const required = !!status?.required;

  return (
    <>
      {/* Row — mirrors the "Enable 2FA Authentication" row above it */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 5,
          pl: 2,
          pr: 2.5,
          mt: 3,
        }}
        data-testid="authenticator-row"
      >
        <Box sx={{ textAlign: "left", flexGrow: 1 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography fontSize="14px" fontWeight={400} color="#60768E">
              Authenticator App (2FA)
            </Typography>
            {!isLoading &&
              (enabled ? (
                <Chip
                  icon={<CheckCircle sx={{ fontSize: 16 }} />}
                  label="Enabled"
                  color="success"
                  size="small"
                  variant="outlined"
                  data-testid="authenticator-status-enabled"
                />
              ) : (
                <Chip label="Not set up" size="small" data-testid="authenticator-status-disabled" />
              ))}
          </Stack>
          <Typography fontSize="14px" fontWeight={400} color="#123257" sx={{ mt: 0.5 }}>
            Use Microsoft Authenticator, Google Authenticator, or Authy for a stronger, email-free
            code at login.
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", minWidth: 120, justifyContent: "flex-end" }}>
          {isLoading ? (
            <CircularProgress size={20} />
          ) : enabled && required ? (
            <Typography fontSize={12} color="#60768E" sx={{ fontStyle: "italic" }}>
              Required by your admin
            </Typography>
          ) : enabled ? (
            <Button
              variant="outlined"
              color="error"
              onClick={() => setDisableOpen(true)}
              data-testid="authenticator-disable-open"
              sx={{ borderRadius: "10px", textTransform: "none", px: 2.5, fontWeight: 500 }}
            >
              Disable
            </Button>
          ) : (
            <Button
              variant="outlined"
              onClick={openEnroll}
              data-testid="authenticator-setup-start"
              sx={OUTLINED_BTN}
            >
              Set up
            </Button>
          )}
        </Box>
      </Box>

      {/* Enroll dialog */}
      <Dialog
        open={enrollOpen}
        onClose={() => setEnrollOpen(false)}
        PaperProps={{ sx: { width: "100%", maxWidth: 420, m: 2 } }}
      >
        <DialogTitle sx={{ color: "#123257", fontWeight: 600 }}>
          {backupCodes ? "Save your backup codes" : "Set up authenticator app"}
        </DialogTitle>
        <DialogContent dividers>
          {backupCodes ? (
            <Stack spacing={1.5}>
              <Alert severity="success" icon={<CheckCircle />}>
                Authenticator enabled.
              </Alert>
              <Typography fontSize={13} color="text.secondary">
                Save these backup codes somewhere safe. Each works once if you lose your device.
              </Typography>
              <Box
                data-testid="authenticator-backup-codes"
                sx={{
                  fontFamily: "Consolas, monospace",
                  fontSize: 15,
                  lineHeight: 2,
                  columns: 2,
                  background: "#F7F9FC",
                  border: "1px solid #E2E7EF",
                  borderRadius: 1,
                  p: 1.5,
                }}
              >
                {backupCodes.map((c) => (
                  <div key={c}>{c}</div>
                ))}
              </Box>
            </Stack>
          ) : startM.isPending || !uri ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={2} alignItems="center">
              <Typography fontSize={13} color="text.secondary" alignSelf="flex-start">
                1. Scan this QR in your authenticator app:
              </Typography>
              <Box sx={{ p: 1.5, background: "#fff", border: "1px solid #E2E7EF", borderRadius: 2 }}>
                <QRCodeSVG value={uri} size={168} />
              </Box>
              <Box sx={{ width: "100%" }}>
                <Typography fontSize={12} color="text.secondary">
                  Can't scan? Enter this key manually:
                </Typography>
                <Box
                  data-testid="authenticator-manual-key"
                  sx={{
                    fontFamily: "Consolas, monospace",
                    fontSize: 14,
                    letterSpacing: 1,
                    background: "#FFF8E6",
                    border: "1px solid #F0D98A",
                    borderRadius: 1,
                    px: 1.5,
                    py: 1,
                    mt: 0.5,
                    userSelect: "all",
                    wordBreak: "break-all",
                  }}
                >
                  {manualKey}
                </Box>
              </Box>
              <Divider flexItem />
              <TextField
                data-testid="authenticator-enroll-code"
                label="2. Enter the 6-digit code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                inputProps={{ inputMode: "numeric", maxLength: 6, style: { letterSpacing: 6 } }}
                fullWidth
                autoFocus
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {backupCodes ? (
            <Button
              variant="contained"
              onClick={() => {
                setEnrollOpen(false);
                setBackupCodes(null);
              }}
              sx={{ textTransform: "none", borderRadius: "10px" }}
            >
              Done
            </Button>
          ) : (
            <>
              <Button onClick={() => setEnrollOpen(false)} sx={{ textTransform: "none", color: "#60768E" }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={verifyEnroll}
                disabled={verifyM.isPending || code.length !== 6 || !uri}
                data-testid="authenticator-enroll-verify"
                sx={{ textTransform: "none", borderRadius: "10px" }}
              >
                {verifyM.isPending ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Verify & enable"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Disable dialog */}
      <Dialog
        open={disableOpen}
        onClose={() => setDisableOpen(false)}
        PaperProps={{ sx: { width: "100%", maxWidth: 420, m: 2 } }}
      >
        <DialogTitle sx={{ color: "#123257", fontWeight: 600 }}>Disable authenticator</DialogTitle>
        <DialogContent dividers>
          <Typography fontSize={13} color="text.secondary" sx={{ mb: 2 }}>
            Enter a current code from your authenticator app (or a backup code) to turn it off.
          </Typography>
          <TextField
            data-testid="authenticator-disable-code"
            label="Authenticator or backup code"
            value={disableCode}
            onChange={(e) => setDisableCode(e.target.value)}
            fullWidth
            autoFocus
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDisableOpen(false)} sx={{ textTransform: "none", color: "#60768E" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={doDisable}
            disabled={disableM.isPending || !disableCode.trim()}
            data-testid="authenticator-disable-submit"
            sx={{ textTransform: "none", borderRadius: "10px" }}
          >
            {disableM.isPending ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Disable"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
